import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Building2, Droplets, Zap, Heart, Monitor, TreePine, Eye } from 'lucide-react';
import { formatCurrency, getDepartmentColor } from '../../data/mockData';

const DepartmentCards = ({ departmentStats, onDepartmentClick, selectedDepartment }) => {
  const getDepartmentIcon = (department) => {
    const iconProps = { size: 28, className: "text-white" };
    switch (department) {
      case 'PWD': return <Building2 {...iconProps} />;
      case 'Water Supply': return <Droplets {...iconProps} />;
      case 'Electrical': return <Zap {...iconProps} />;
      case 'Health': return <Heart {...iconProps} />;
      case 'IT': return <Monitor {...iconProps} />;
      case 'Garden': return <TreePine {...iconProps} />;
      default: return <Building2 {...iconProps} />;
    }
  };

  const handleViewProjects = (department) => {
    onDepartmentClick(department);
    
    // Auto-scroll to project listing section
    setTimeout(() => {
      const projectSection = document.getElementById('project-listing-section');
      if (projectSection) {
        projectSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Define the specific order for department cards
  const departmentOrder = [
    'PWD',
    'Water Supply', 
    'Electrical',
    'Health',
    'Garden',
    'IT'
  ];

  // Sort departments according to the specified order
  const sortedDepartments = [...departmentStats].sort((a, b) => {
    const indexA = departmentOrder.indexOf(a.department);
    const indexB = departmentOrder.indexOf(b.department);
    
    // If department not found in order list, put it at the end
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    
    return indexA - indexB;
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {sortedDepartments.map((dept) => (
        <Card
          key={dept.department}
          className="p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border-0 hover:shadow-xl"
        >
          <div className={`bg-gradient-to-br ${getDepartmentColor(dept.department)} p-4 rounded-xl shadow-lg mb-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getDepartmentIcon(dept.department)}
                <h3 className="text-lg font-bold text-white">{dept.department}</h3>
              </div>
              <Badge variant="secondary" className="bg-white/20 text-white border-0">
                Active
              </Badge>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600 font-medium">Total Projects</span>
              <span className="text-2xl font-bold text-slate-800">{dept.total_projects || 0}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600 font-medium">Sanctioned Amount</span>
              <span className="text-lg font-bold text-teal-600">{formatCurrency(dept.total_sanctioned_amount || 0)}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100">
            <Button 
              onClick={() => handleViewProjects(dept.department)}
              className="w-full bg-white text-slate-700 border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700 font-semibold py-3 px-4 rounded-lg transition-all duration-300 shadow-sm hover:shadow-md group"
            >
              <Eye className="h-4 w-4 mr-2 group-hover:text-teal-600 transition-colors" />
              View Projects
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default DepartmentCards;